import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { AuthContext } from "../../context/AuthContext";
import { admins } from "../../Constants";
import Loading from "../../components/Loading";
import {
    UnsignedMatch,
    UnsignedPlayer,
    UnsignedTeam,
    getUnsignedTeams,
    setHidden,
    setMention,
    setStream,
    uploadPlayerPhoto,
} from "../../services/UnsignedGraphicsService";
import { CARD_FONT, CARD_H, CARD_W, CardAssets, CardData, DURATION, MAX_PLAYERS, MAX_ROWS, STILL_T, loadCardAssets, renderToCanvas } from "./cardRenderer";
import { exportCardVideo } from "./videoExport";
import "./unsignedCard.css"; // the card font; the canvas draws with it once loaded
import { TBA, WeekChoice, X_LIMIT, buildCaption, captionTime, matchesInWeek, xLength } from "./unsignedGraphics";

// Admin tool replacing the weekly Canva work for Unsigned: a schedule card + X caption per team.
// See tasks/unsigned-graphics.md.

const MY_STREAM = "twitch.tv/realxguys";

const panel: React.CSSProperties = {
    background: "linear-gradient(135deg, rgba(26,26,26,0.95) 0%, rgba(37,37,37,0.95) 100%)",
    border: "1px solid rgba(255,102,0,0.15)",
    borderRadius: 12,
    padding: "16px 20px",
    marginBottom: 24,
};
const muted: React.CSSProperties = { color: "rgba(255,255,255,0.6)", fontSize: "0.85rem" };
const input: React.CSSProperties = {
    background: "#1a1a1a", color: "#e2e8f0", border: "1px solid #333", borderRadius: 6,
    padding: "4px 8px", fontSize: "0.85rem",
};
const button: React.CSSProperties = {
    background: "#f15c25", color: "#fff", border: "none", borderRadius: 6,
    padding: "6px 14px", fontWeight: 600, fontSize: "0.85rem", cursor: "pointer",
};
const ghostButton: React.CSSProperties = { ...button, background: "transparent", border: "1px solid #444" };

/**
 * The five who go on the card: whoever has played most for the team this season, so a sub on the
 * lineup (or someone with a nice photo) doesn't push out a starter. Ties go to players with a photo.
 * The renderer decides who stands where.
 */
const cardPlayers = (players: UnsignedPlayer[]) =>
    players
        .filter(p => !p.hidden)
        .map((p, i) => ({ p, i }))
        .sort((a, b) => b.p.gamesPlayed - a.p.gamesPlayed
            || Number(b.p.hasPhoto) - Number(a.p.hasPhoto) || a.i - b.i)
        .map(x => x.p)
        .slice(0, MAX_PLAYERS);

const chunk = <T,>(xs: T[], n: number): T[][] => {
    const out: T[][] = [];
    for (let i = 0; i < xs.length; i += n) out.push(xs.slice(i, i + n));
    return out;
};

const download = (href: string, fileName: string) => {
    const a = document.createElement("a");
    a.download = fileName;
    a.href = href;
    a.click();
};

const PREVIEW_SCALE = 1.5; // crisp when shown up to 1920px wide

const CardPreview: React.FC<{
    team: UnsignedTeam; matches: UnsignedMatch[]; players: UnsignedPlayer[];
    photoVersion: number; fileName: string;
}> = ({ team, matches, players, photoVersion, fileName }) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [assets, setAssets] = useState<CardAssets | null>(null);
    const [busy, setBusy] = useState<string | null>(null);
    const playing = useRef<number | null>(null);

    const card: CardData = useMemo(
        () => ({ teamId: team.teamId, teamName: team.teamName, matches, players }),
        [team.teamId, team.teamName, matches, players]);

    useEffect(() => {
        let live = true;
        setAssets(null);
        loadCardAssets(card, photoVersion).then(a => live && setAssets(a));
        return () => { live = false; };
    }, [card, photoVersion]);

    const drawAt = useCallback((t: number) => {
        if (canvasRef.current && assets) renderToCanvas(canvasRef.current, PREVIEW_SCALE, t, card, assets);
    }, [assets, card]);

    useEffect(() => { drawAt(STILL_T); }, [drawAt]);
    useEffect(() => () => { if (playing.current) cancelAnimationFrame(playing.current); }, []);

    // Plays the intro and settles on the hold - the part worth watching; the rest is the still.
    const play = () => {
        if (playing.current) cancelAnimationFrame(playing.current);
        const t0 = performance.now();
        const tick = (now: number) => {
            const t = (now - t0) / 1000;
            drawAt(Math.min(t, STILL_T));
            playing.current = t < STILL_T ? requestAnimationFrame(tick) : null;
        };
        playing.current = requestAnimationFrame(tick);
    };

    const savePng = () => {
        if (!assets) return;
        const c = document.createElement("canvas");
        renderToCanvas(c, 2, STILL_T, card, assets); // 2560x1440 - the figures are rendered at 2x for this
        download(c.toDataURL("image/png"), `${fileName}.png`);
    };

    const saveVideo = async () => {
        if (!assets) return;
        setBusy("Encoding 0%");
        try {
            const blob = await exportCardVideo(card, assets, f => setBusy(`Encoding ${Math.round(f * 100)}%`));
            const url = URL.createObjectURL(blob);
            download(url, `${fileName}.mp4`);
            setTimeout(() => URL.revokeObjectURL(url), 10_000);
        } catch (e) {
            console.error("Video export failed:", e);
            alert(`Video export failed: ${e instanceof Error ? e.message : e}`);
        } finally {
            setBusy(null);
        }
    };

    return (
        <div style={{ marginBottom: 12 }}>
            <div style={{ position: "relative", width: "100%", maxWidth: CARD_W, aspectRatio: `${CARD_W} / ${CARD_H}`,
                background: "#0b0d24", borderRadius: 6, overflow: "hidden" }}>
                <canvas ref={canvasRef} style={{ width: "100%", height: "100%", display: "block" }} />
                {!assets && <div style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center" }}><Loading /></div>}
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                <button style={ghostButton} onClick={play} disabled={!assets}>Play intro</button>
                <button style={button} onClick={savePng} disabled={!assets || !!busy}>Download PNG</button>
                <button style={button} onClick={saveVideo} disabled={!assets || !!busy}>
                    {busy ?? `Download video (${DURATION}s)`}
                </button>
            </div>
        </div>
    );
};
const StreamPicker: React.FC<{ match: UnsignedMatch; onSave: (stream: string) => void }> = ({ match, onSave }) => {
    const current = match.stream ?? "";
    const mode = current === "" ? "tba" : current === MY_STREAM ? "mine" : "custom";
    const [custom, setCustom] = useState(mode === "custom" ? current : "");
    return (
        <span style={{ display: "inline-flex", gap: 6 }}>
            <select style={input} value={mode} onChange={e => {
                const v = e.target.value;
                if (v === "tba") onSave("");
                else if (v === "mine") onSave(MY_STREAM);
                else onSave(custom || "twitch.tv/");
            }}>
                <option value="tba">{TBA}</option>
                <option value="mine">{MY_STREAM}</option>
                <option value="custom">Other...</option>
            </select>
            {mode === "custom" && (
                <input style={{ ...input, width: 200 }} value={custom} onChange={e => setCustom(e.target.value)}
                    onBlur={() => custom !== current && onSave(custom)} placeholder="twitch.tv/..." />
            )}
        </span>
    );
};

const MentionInput: React.FC<{ value: string; placeholder: string; onSave: (v: string) => void }> = ({ value, placeholder, onSave }) => {
    const [v, setV] = useState(value);
    useEffect(() => setV(value), [value]);
    return (
        <input style={{ ...input, width: 220 }} value={v} placeholder={placeholder}
            onChange={e => setV(e.target.value)} onBlur={() => v !== value && onSave(v)} />
    );
};

const TeamSection: React.FC<{
    team: UnsignedTeam; week: WeekChoice; mentions: Record<string, string>; photoVersion: number;
    onMention: (teamId: string, mention: string) => void;
    onStream: (challongeId: string, stream: string) => void;
    onHidden: (steamId: string, hidden: boolean) => void;
    onUpload: (steamId: string, file: File) => void;
}> = ({ team, week, mentions, photoVersion, onMention, onStream, onHidden, onUpload }) => {
    const matches = useMemo(() => matchesInWeek(team.matches, week), [team.matches, week]);
    const players = useMemo(() => cardPlayers(team.players), [team.players]);
    const generated = useMemo(() => buildCaption(team.teamName, matches, mentions, week), [team.teamName, matches, mentions, week]);

    // The caption can be hand-edited before copying; a regenerated one only replaces it on Reset.
    const [edited, setEdited] = useState<string | null>(null);
    const caption = edited ?? generated;
    const length = xLength(caption);
    const [copied, setCopied] = useState(false);

    if (matches.length === 0) {
        return (
            <div style={panel}>
                <h4 style={{ margin: 0 }}>{team.teamName}</h4>
                <div style={muted}>No matches {week === "next" ? "next week" : "left this week"} - no card.</div>
            </div>
        );
    }

    const stamp = captionTime(matches[0].matchTimeUtc).split(" ")[0].replace("/", "-");
    const cards = chunk(matches, MAX_ROWS);

    return (
        <div style={panel}>
            <h4 style={{ marginTop: 0 }}>{team.teamName} <span style={muted}>{team.matches[0]?.division}</span></h4>

            {cards.map((rows, i) => (
                <CardPreview key={i} team={team} matches={rows} players={players} photoVersion={photoVersion}
                    fileName={`${team.teamName.replace(/\s+/g, "-").toLowerCase()}-${stamp}${cards.length > 1 ? `-${i + 1}` : ""}`} />
            ))}

            <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)", gap: 20, marginTop: 8 }}>
                <div>
                    <div style={{ fontWeight: 600, marginBottom: 6 }}>Caption</div>
                    <textarea style={{ ...input, width: "100%", minHeight: 260, fontFamily: "inherit" }}
                        value={caption} onChange={e => setEdited(e.target.value)} />
                    <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 6 }}>
                        <button style={button} onClick={async () => {
                            await navigator.clipboard.writeText(caption);
                            setCopied(true);
                            setTimeout(() => setCopied(false), 1500);
                        }}>{copied ? "Copied" : "Copy caption"}</button>
                        {edited !== null && <button style={ghostButton} onClick={() => setEdited(null)}>Reset</button>}
                        <span style={{ ...muted, color: length > X_LIMIT ? "#f87171" : muted.color }}>
                            {length}/{X_LIMIT}{length > X_LIMIT && " - too long for a normal X post"}
                        </span>
                    </div>
                </div>

                <div>
                    <div style={{ fontWeight: 600, marginBottom: 6 }}>Matches</div>
                    {matches.map(m => (
                        <div key={m.challongeId} style={{ marginBottom: 10 }}>
                            <div>{captionTime(m.matchTimeUtc)} vs <b>{m.opponent.teamName}</b></div>
                            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 4 }}>
                                <MentionInput value={mentions[m.opponent.teamId] ?? ""} placeholder={`X mention, e.g. @${m.opponent.teamName.replace(/\s+/g, "")}`}
                                    onSave={v => onMention(m.opponent.teamId, v)} />
                                <StreamPicker match={m} onSave={s => onStream(m.challongeId, s)} />
                            </div>
                        </div>
                    ))}

                    <div style={{ fontWeight: 600, margin: "14px 0 6px" }}>Players</div>
                    {team.players.map(p => (
                        <div key={p.steamId} style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 4,
                            opacity: players.includes(p) ? 1 : 0.55 }}>
                            <input type="checkbox" checked={!p.hidden} onChange={e => onHidden(p.steamId, !e.target.checked)} />
                            <span style={{ minWidth: 110 }}>{p.nickname}</span>
                            <span style={{ ...muted, minWidth: 60 }}>{p.gamesPlayed} maps</span>
                            <span style={{ ...muted, minWidth: 80 }}>{p.hasPhoto ? "photo" : "silhouette"}</span>
                            <span style={{ ...muted, minWidth: 80 }}>{players.includes(p) ? "on card" : p.hidden ? "hidden" : "not on card"}</span>
                            <label style={{ ...ghostButton, padding: "2px 10px", margin: 0 }}>
                                {p.hasPhoto ? "Replace photo" : "Upload photo"}
                                <input type="file" accept="image/png,image/jpeg" style={{ display: "none" }}
                                    onChange={e => { const f = e.target.files?.[0]; if (f) onUpload(p.steamId, f); e.target.value = ""; }} />
                            </label>
                        </div>
                    ))}
                    {team.players.filter(p => !p.hidden).length > MAX_PLAYERS && (
                        <div style={muted}>A card fits {MAX_PLAYERS}: the most-played are used. Untick anyone who shouldn't be on it.</div>
                    )}
                </div>
            </div>
        </div>
    );
};

const UnsignedGraphicsPage: React.FC = () => {
    const { user, authLoaded } = useContext(AuthContext);
    const isAdmin = !!user && admins.includes(user.steamID);

    const [teams, setTeams] = useState<UnsignedTeam[] | null>(null);
    const [mentions, setMentions] = useState<Record<string, string>>({});
    const [week, setWeek] = useState<WeekChoice>("this");
    const [error, setError] = useState<string | null>(null);
    const [fontsReady, setFontsReady] = useState(false);
    const [photoVersion, setPhotoVersion] = useState(0);

    const load = useCallback(() => {
        getUnsignedTeams()
            .then(r => { setTeams(r.teams); setMentions(r.mentions); })
            .catch(() => setError("Could not load the Unsigned teams."));
    }, []);

    useEffect(() => {
        if (isAdmin) load();
    }, [isAdmin, load]);

    // Name fitting measures text in the card font, so wait for it before laying out any card.
    useEffect(() => {
        document.fonts.load(`700 30px ${CARD_FONT}`).finally(() => setFontsReady(true));
    }, []);

    const fail = (what: string) => () => setError(`Could not save ${what}.`);

    const onMention = (teamId: string, mention: string) => {
        setMentions(prev => ({ ...prev, [teamId]: mention }));
        setMention(teamId, mention).catch(fail("the mention"));
    };

    const onStream = (challongeId: string, stream: string) => {
        setTeams(prev => prev?.map(t => ({
            ...t, matches: t.matches.map(m => m.challongeId === challongeId ? { ...m, stream: stream || null } : m),
        })) ?? prev);
        setStream(challongeId, stream).catch(fail("the stream"));
    };

    const onHidden = (teamId: string) => (steamId: string, hidden: boolean) => {
        setTeams(prev => prev?.map(t => t.teamId !== teamId ? t : {
            ...t, players: t.players.map(p => p.steamId === steamId ? { ...p, hidden } : p),
        }) ?? prev);
        setHidden(teamId, steamId, hidden).catch(fail("the player"));
    };

    const onUpload = async (steamId: string, file: File) => {
        try {
            await uploadPlayerPhoto(steamId, file);
            setPhotoVersion(Date.now());
            load();
        } catch {
            setError("Photo upload failed.");
        }
    };

    if (!authLoaded) return <div className="text-center" style={{ paddingTop: 60 }}><Loading /></div>;
    if (!isAdmin) return <div className="container mt-4"><div className="alert alert-danger">Admins only.</div></div>;

    return (
        <div className="container mt-4" style={{ color: "var(--text-primary)", maxWidth: 1400 }}>
            <h1 style={{ color: "#e2e8f0", fontWeight: 700 }}>Unsigned graphics</h1>
            <p style={muted}>
                Schedule card + X caption per Unsigned team, from the OverPass fixtures. Times are Danish
                (CET/CEST). Mentions and streams are remembered; photo uploads go on the player's PowerStats profile.
            </p>

            <div style={{ display: "flex", gap: 8, margin: "16px 0 24px" }}>
                {(["this", "next"] as WeekChoice[]).map(w => (
                    <button key={w} style={week === w ? button : ghostButton} onClick={() => setWeek(w)}>
                        {w === "this" ? "Rest of this week" : "Next week"}
                    </button>
                ))}
            </div>

            {error && <div className="alert alert-danger" onClick={() => setError(null)}>{error}</div>}

            {!teams || !fontsReady
                ? <div className="text-center" style={{ paddingTop: 40 }}><Loading /></div>
                : teams.length === 0
                    ? <div style={muted}>No Unsigned teams in the current OverPass data.</div>
                    : teams.map(t => (
                        <TeamSection key={t.teamId} team={t} week={week} mentions={mentions} photoVersion={photoVersion}
                            onMention={onMention} onStream={onStream} onHidden={onHidden(t.teamId)} onUpload={onUpload} />
                    ))}
        </div>
    );
};

export default UnsignedGraphicsPage;
